import React from "react";
import {
  DEFAULT_ASSISTANT_PREFERENCES,
  DEFAULT_ASSISTANT_UI_SETTINGS,
} from "../constants/assistantSettings";

const getStorageKey = (userId) =>
  `ai-assistant-ui-settings::${String(userId || "anonymous")}`;

const normalizePreferences = (preferences = {}) => ({
  language:
    typeof preferences?.language === "string" && preferences.language.trim()
      ? preferences.language
      : DEFAULT_ASSISTANT_PREFERENCES.language,
});

const cloneDefaults = () => ({
  preferences: normalizePreferences(),
});

const loadStoredUiSettings = (userId) => {
  if (typeof window === "undefined") return cloneDefaults();

  try {
    const raw = window.localStorage.getItem(getStorageKey(userId));
    if (!raw) return cloneDefaults();
    const parsed = JSON.parse(raw);
    return {
      preferences: normalizePreferences(parsed?.preferences || {}),
    };
  } catch {
    return cloneDefaults();
  }
};

export const getStoredAssistantPreferences = (userId) =>
  loadStoredUiSettings(userId).preferences;

export const useAssistantSettingsState = ({ userId }) => {
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [preferencesDraft, setPreferencesDraft] = React.useState(
    DEFAULT_ASSISTANT_PREFERENCES,
  );
  const [savedPreferences, setSavedPreferences] = React.useState(
    DEFAULT_ASSISTANT_PREFERENCES,
  );

  React.useEffect(() => {
    const storedSettings = loadStoredUiSettings(userId);
    setSavedPreferences(storedSettings.preferences);
    setPreferencesDraft(storedSettings.preferences);
  }, [userId]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const payload = JSON.stringify({
        preferences: savedPreferences,
      });
      window.localStorage.setItem(getStorageKey(userId), payload);
    } catch {
      // Keep in-memory preferences usable when storage is unavailable.
    }
  }, [savedPreferences, userId]);

  const openSettings = () => {
    setIsSettingsOpen(true);
  };

  const closeSettings = () => {
    setPreferencesDraft(savedPreferences);
    setIsSettingsOpen(false);
  };

  const updatePreferences = (nextPreferences) => {
    setPreferencesDraft((currentPreferences) => {
      const resolvedPreferences =
        typeof nextPreferences === "function"
          ? nextPreferences(currentPreferences)
          : nextPreferences;
      const normalizedPreferences = normalizePreferences(resolvedPreferences);
      setSavedPreferences(normalizedPreferences);
      return normalizedPreferences;
    });
  };

  return {
    isSettingsOpen,
    savedPreferences,
    preferencesDraft,
    openSettings,
    closeSettings,
    setPreferencesDraft: updatePreferences,
  };
};
